import { ClientOnly, Link } from "@tanstack/react-router";
import { Component, lazy, type ReactNode, type Ref, type RefObject, Suspense } from "react";

import { ErrorHandler } from "@/web/lib/errors";
import { GenericCardLoader } from "@/web/routes/browse/-components/GenericCardLoader";

/**
 * The rail (browse cards, their dialogs, the browse data hook) is loaded with a
 * dynamic import. The homepage is part of the entry chunk; importing the browse
 * card components statically made the entry and the shared card chunk import
 * each other, and in production whichever evaluated first saw the other's
 * exports uninitialized ("r is not a function" — `/` and `/browse` broke on
 * staging). A dynamic import only runs after the entry has fully evaluated.
 */
const FeaturedRail = lazy(() => import("@/web/routes/-home/FeaturedRail"));

/** Must match CARD_SLOT in FeaturedRail (the placeholder stands in for its cards). */
const CARD_SLOT = "offering-card w-[340px] shrink-0 snap-start max-[390px]:w-[calc(100vw-48px)]";

/**
 * Four card-sized skeletons, shown during SSR and while the rail's chunk loads.
 * Kept in this file (not FeaturedRail's `RailLoading`) so it needs no lazy code.
 */
function RailPlaceholder() {
  return (
    <div
      className="-mr-(--gutter) flex gap-[22px] overflow-hidden pt-[5px] pr-(--gutter) pb-[15px]"
      aria-hidden="true"
    >
      {Array.from({ length: 4 }).map((_, i) => (
        // oxlint-disable-next-line react/no-array-index-key skeleton placeholders
        <div key={i} className={CARD_SLOT}>
          <GenericCardLoader />
        </div>
      ))}
    </div>
  );
}

/**
 * "PUT STOCK IN YOUR NEIGHBORHOOD" — the first four `/browse` results in its
 * default order (fundraising cards first), rendered with the real browse cards. The heading is SSR'd (and
 * carries the nav theme); the rail is client-only because the cards need Clerk
 * and SpacetimeDB.
 *
 * `railAnchor` is always rendered at the rail's position so the story handoff
 * can time itself even when the rail is empty; `handoffTargetRef` lands on the
 * first real card.
 */
export function FeaturedOfferings({
  railAnchor,
  handoffTargetRef,
  onRailScroll,
}: {
  railAnchor: RefObject<HTMLDivElement | null>;
  handoffTargetRef: Ref<HTMLDivElement>;
  onRailScroll: () => void;
}) {
  return (
    <section
      id="opportunities"
      data-nav-theme="white"
      className="scroll-mt-(--nav-h) overflow-hidden px-(--gutter) pt-[100px] pb-[65px] max-[760px]:pt-[65px]"
    >
      <div className="mb-[50px] flex items-end justify-between gap-[50px] max-[760px]:mb-[30px] max-[760px]:block">
        <div>
          <span className="mb-[25px] block text-[12px] leading-[1.5] tracking-[0.12em] max-[760px]:text-[10px] max-[760px]:tracking-[0.1em]">
            REAL PLACES. SHARED POSSIBILITIES.
          </span>
          <h2 className="font-heading text-[clamp(28px,3vw,40px)] leading-[1.08] font-black tracking-[-0.025em] uppercase">
            PUT STOCK IN
            <br />
            YOUR NEIGHBORHOOD
          </h2>
        </div>
        <div className="max-w-[350px] text-[19px] leading-[1.5] max-[760px]:mt-[25px] max-[760px]:text-[17px]">
          <Link
            to="/browse"
            className="mt-[25px] inline-flex items-center gap-[26px] border-b border-current pb-[10px] text-[15px] leading-[1.3] text-[#2f2f8c] hover:opacity-70"
          >
            Browse all offerings ↗
          </Link>
        </div>
      </div>
      <div ref={railAnchor}>
        <ClientOnly fallback={<RailPlaceholder />}>
          <RailErrorBoundary>
            <Suspense fallback={<RailPlaceholder />}>
              <FeaturedRail handoffTargetRef={handoffTargetRef} onRailScroll={onRailScroll} />
            </Suspense>
          </RailErrorBoundary>
        </ClientOnly>
      </div>
    </section>
  );
}

/**
 * If the rail's chunk fails to load (e.g. a stale chunk right after a deploy) or
 * the rail throws, drop just the rail — the same outcome as a data error — rather
 * than letting it reach the root boundary and replace the whole homepage. The
 * story's handoff already skips itself when no first card is attached.
 */
class RailErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(error: unknown) {
    ErrorHandler.logError(error);
  }

  override render() {
    return this.state.failed ? null : this.props.children;
  }
}
